import React, { useMemo, useState } from "react";

import { CalendarDay } from "../calendar/utils";
import { StickyContainer } from "../sticky/StickyContainer";
import { useSettings } from "../utils/settings";
import { useTweaks } from "../utils/TweakProvider";
import { PlayableTitle } from "./audio/PlayableTitle";
import { TonePlayer } from "./audio/TonePlayer";
import { Chant } from "./Chant";
import { INVITATORY_ANTIPHONS } from "./invitatory-antiphons";
import {
    CanticleID,
    getEasierTone,
    getPointedCanticle,
    getPointedText,
} from "./utils";

export const getCanticleTitle = (id: string) => {
    return id === "venite"
        ? "Venite"
        : id === "te deum"
        ? "Te Deum"
        : id === "benedictus"
        ? "Benedictus"
        : id === "magnificat"
        ? "Magnificat"
        : id === "nunc dimittis"
        ? "Nunc Dimittis"
        : id === "pascha nostrum"
        ? "Pascha Nostrum"
        : id === "benedictus es"
        ? "Benedictus es, Domine"
        : "";
};

export const CANTICLE_TONES = {
    venite: ["1a.1", "2.1", "8.5", "6b"],
    "pascha nostrum": [
        "Tonus Peregrinus A",
        "Tonus Peregrinus A",
        "Tonus Peregrinus A",
        "Tonus Peregrinus A",
    ],
    "te deum": ["3a.6", "Tonus Peregrinus A", "7.5", "8.1"],
    "benedictus es": ["3a.6", "Tonus Peregrinus A", "7.5", "8.1"],
    benedictus: ["6a", "3a.1", "4.4", "2.1"],
    magnificat: ["2.1", "6b", "7.5", "1a.1"],
    "nunc dimittis": ["3a.4", "1a.2", "4.8", "6a"],
    "quicunque vult": ["4.4", "4.4", "4.4", "4.4"],
};

export const Canticle: React.FC<{
    id: CanticleID;
    day: CalendarDay;
    tone?: string;
    exportLines?: boolean;
}> = (props) => {
    const { id, day, tone: toneOverride, exportLines } = props;

    const { settings } = useSettings();

    const defaultTone =
        toneOverride ?? CANTICLE_TONES[id][day.weekOfTheYear % 4];

    const { tweaks, setTweak } = useTweaks();
    let tone = tweaks[id] ?? defaultTone;

    const [antiphonModal, setAntiphonModal] = useState(<></>);
    const [canticleModal, setCanticleModal] = useState(<></>);

    const antiphon = (() => {
        if (
            id === "venite" &&
            (settings.invitatoryAntiphons === "always" ||
                (settings.invitatoryAntiphons === "seasons" &&
                    !day.antiphonId.includes("ordinary")))
        ) {
            const antiphonTone =
                settings.antiphonTones === "solemn"
                    ? tone.includes("Tonus")
                        ? tone
                        : tone[0] + "s" + tone.replace(/[ab]/, "").slice(1)
                    : tone;

            //const antiphonTone = tone.slice(0, 1) + "ant";

            return (
                <StickyContainer id={`${id}-ant`}>
                    <PlayableTitle
                        id={`${id}-ant`}
                        title="Antiphon"
                        player={
                            <TonePlayer
                                tones={[antiphonTone]}
                                selectedTone={antiphonTone}
                                id={"antiphon"}
                                onSetModal={(id, element) =>
                                    setAntiphonModal(element)
                                }
                            />
                        }
                    />
                    <Chant
                        pointedText={getPointedText(
                            [INVITATORY_ANTIPHONS[day.antiphonId]],
                            antiphonTone,
                            false,
                            true
                        )}
                        boldMode="none"
                        dropCap={false}
                        exportLines={exportLines}
                        tone={antiphonTone}
                    />
                </StickyContainer>
            );
        }
    })();

    const content = useMemo(() => {
        const pointedPsalm = getPointedCanticle(id, tone);

        return (
            <Chant
                pointedText={pointedPsalm}
                boldMode={settings.psalmsBold}
                dropCap={true}
                exportLines={exportLines}
                tone={tone}
            />
        );
    }, [id, tone, settings.psalmsBold]);

    return (
        <div style={{}}>
            {antiphon}
            <StickyContainer id={id}>
                <PlayableTitle
                    id={id}
                    title={getCanticleTitle(id)}
                    player={
                        <TonePlayer
                            tones={[tone, getEasierTone(tone)]}
                            selectedTone={tone}
                            id={id}
                            onSetModal={(id, element) =>
                                setAntiphonModal(element)
                            }
                            onSetSelectedTone={(tone) => {
                                setTweak(id, tone);
                            }}
                        />
                    }
                />
                {content}
            </StickyContainer>
            {antiphon}
            {antiphonModal}
            {canticleModal}
        </div>
    );
};
